import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GetAdminSignupsAPI } from '../../../../api/AdminAPI';

const DEFAULT_PAGINATION = { total: 0, page: 1, limit: 20, pages: 1 };
const DEFAULT_SUMMARY = { pending: 0, expired: 0, confirmed: 0, total: 0 };
const SEARCH_DEBOUNCE_MS = 400;

/**
 * useAdminSignups
 *
 * Hook responsável pela tela "Cadastros pendentes" do Admin Geral: lista paginada/filtrada
 * de pending_signups (list.php). Filtros (status, busca, página) vivem na URL via
 * useSearchParams, no mesmo padrão de useAdminAudit, para que o link possa ser compartilhado.
 *
 * @returns {Object} estado e ações da tela
 */
function useAdminSignups() {
  const [searchParams, setSearchParams] = useSearchParams();

  const updateParams = useCallback((updates, resetPage = true) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          next.delete(key);
        } else {
          next.set(key, String(value));
        }
      });
      if (resetPage) next.delete('page');
      return next;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Filtros (na URL) ───────────────────────────────────────────────────────
  const status = searchParams.get('status') || 'all';
  const q = searchParams.get('search') || '';
  const page = parseInt(searchParams.get('page'), 10) || 1;

  const setStatus = useCallback((value) => updateParams({ status: value === 'all' ? undefined : value }), [updateParams]);
  const setPage = useCallback((value) => updateParams({ page: value }, false), [updateParams]);

  // ─── Busca (draft local + debounce -> URL) ──────────────────────────────────
  const [searchInput, setSearchInputState] = useState(q);
  const searchTimer = useRef(null);

  useEffect(() => { setSearchInputState(q); }, [q]);

  const setSearchInput = useCallback((value) => {
    setSearchInputState(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => updateParams({ search: value }), SEARCH_DEBOUNCE_MS);
  }, [updateParams]);

  useEffect(() => () => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
  }, []);

  // ─── Lista (list.php) ───────────────────────────────────────────────────────
  const [signups, setSignups] = useState([]);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [summary, setSummary] = useState(DEFAULT_SUMMARY);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSignups = useCallback(() => {
    setIsLoading(true);
    setError(null);
    GetAdminSignupsAPI(
      { status, search: q, page, limit: DEFAULT_PAGINATION.limit },
      (response) => {
        if (!response || response.success === false) {
          setError(response?.error || 'Erro ao carregar os cadastros pendentes. Tente novamente.');
          setIsLoading(false);
          return;
        }
        setSignups(response.signups || []);
        setPagination(response.pagination || { ...DEFAULT_PAGINATION, page });
        setSummary(response.summary || DEFAULT_SUMMARY);
        setIsLoading(false);
      }
    );
  }, [status, q, page]);

  useEffect(() => {
    fetchSignups();
  }, [fetchSignups]);

  /**
   * Atualiza uma linha em memória (ex.: após reenvio de confirmação), sem refetch.
   * @param {Object} updated - linha já no shape da API (mapRow)
   */
  const updateSignup = useCallback((updated) => {
    if (!updated) return;
    setSignups((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
  }, []);

  return {
    signups,
    pagination,
    summary,
    isLoading,
    error,

    status,
    setStatus,
    searchInput,
    setSearchInput,
    setPage,

    updateSignup,
    refresh: fetchSignups,
  };
}

export default useAdminSignups;
